import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const StoreDetailPanel = ({ store, onClose, onEdit, onNavigateToLayouts, onNavigateToBranding }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!store) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-soft p-8 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Store" size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">Select a Store</h3>
        <p className="text-muted-foreground">
          Choose a store from the list to view detailed information and manage its data.
        </p>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('bn-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0
    })?.format(amount)?.replace('BDT', '৳');
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'branding', label: 'Branding Items', icon: 'Tag' },
    { id: 'history', label: 'History', icon: 'Clock' }
  ];

  const mockBrandingItems = [
    {
      id: 1,
      brand: 'Samsung',
      type: 'Window Display',
      quantity: 2,
      unitRate: 15000,
      totalCost: 30000,
      status: 'Installed',
      lastUpdated: '2024-09-28'
    },
    {
      id: 2,
      brand: 'Apple',
      type: 'Billboard',
      quantity: 1,
      unitRate: 25000,
      totalCost: 25000,
      status: 'Planned',
      lastUpdated: '2024-09-25'
    },
    {
      id: 3,
      brand: 'Xiaomi',
      type: 'In-Store Static',
      quantity: 4,
      unitRate: 8000,
      totalCost: 32000,
      status: 'Verified',
      lastUpdated: '2024-09-20'
    }
  ];

  const mockHistory = [
    {
      id: 1,
      action: 'Store rent updated',
      details: `Monthly rent changed from ৳${(store?.monthlyRent - 5000)?.toLocaleString('bn-BD')} to ৳${store?.monthlyRent?.toLocaleString('bn-BD')}`,
      timestamp: '2024-09-28 14:30',
      user: 'Admin User'
    },
    {
      id: 2,
      action: 'Layout uploaded',
      details: 'New store layout v2.1 uploaded and set as current',
      timestamp: '2024-09-15 10:15',
      user: 'John Doe'
    },
    {
      id: 3,
      action: 'Store created',
      details: 'Store profile created with initial configuration',
      timestamp: '2024-08-01 09:00',
      user: 'System'
    }
  ];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'installed': return 'bg-success/10 text-success';
      case 'verified': return 'bg-primary/10 text-primary';
      case 'planned': return 'bg-warning/10 text-warning';
      case 'expired': return 'bg-error/10 text-error';
      default: return 'bg-muted/10 text-muted-foreground';
    }
  };

  const totalBrandingCost = mockBrandingItems?.reduce((sum, item) => sum + item?.totalCost, 0);

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Store" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{store?.name}</h2>
              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Icon name="MapPin" size={14} />
                  {store?.location}
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Calendar" size={14} />
                  Since {formatDate(store?.createdAt)}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(store)}
              iconName="Edit"
              iconPosition="left"
            >
              Edit Store
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="lg:hidden"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>
      </div>
      {/* Quick Stats */}
      <div className="p-6 border-b border-border bg-muted/30">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              {store?.size?.toLocaleString('bn-BD')}
            </div>
            <div className="text-sm text-muted-foreground">Square Feet</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              {formatCurrency(store?.monthlyRent)}
            </div>
            <div className="text-sm text-muted-foreground">Monthly Rent</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-accent">
              {formatCurrency(store?.rentPerSqFt)}
            </div>
            <div className="text-sm text-muted-foreground">Per Sq Ft</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">
              {formatCurrency(totalBrandingCost)}
            </div>
            <div className="text-sm text-muted-foreground">Total Branding</div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex overflow-x-auto">
          {tabs?.map((tab) => (
            <Button
              key={tab?.id}
              variant="ghost"
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-shrink-0 px-6 py-3 rounded-none border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name={tab?.icon} size={16} className="mr-2" />
              {tab?.label}
            </Button>
          ))}
        </div>
      </div>
      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Store Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Store ID</label>
                    <div className="text-foreground">{store?.id}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Contact Person</label>
                    <div className="text-foreground">{store?.contactPerson}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Phone</label>
                    <div className="text-foreground">{store?.phone}</div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <div className="text-foreground">{store?.email}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Address</label>
                    <div className="text-foreground">{store?.address}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Layout Status</label>
                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                      store?.hasCurrentLayout 
                        ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                    }`}>
                      <Icon 
                        name={store?.hasCurrentLayout ? 'CheckCircle' : 'AlertCircle'} 
                        size={12} 
                      />
                      {store?.hasCurrentLayout ? 'Current Layout Available' : 'Layout Pending'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={() => onNavigateToBranding(store)}
                  iconName="Calculator"
                  iconPosition="left"
                  className="justify-start"
                >
                  Calculate Branding Costs
                </Button>
                <Button
                  variant="outline"
                  onClick={() => onNavigateToLayouts(store)}
                  iconName="Layout"
                  iconPosition="left"
                  className="justify-start"
                >
                  Manage Store Layouts
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'branding' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-foreground">Branding Items</h3>
              <Button
                variant="default"
                size="sm"
                onClick={() => onNavigateToBranding(store)}
                iconName="Plus"
                iconPosition="left"
              >
                Add Item
              </Button>
            </div>
            
            <div className="space-y-3">
              {mockBrandingItems?.map((item) => (
                <div key={item?.id} className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center">
                        <Icon name="Tag" size={16} className="text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{item?.brand}</div>
                        <div className="text-sm text-muted-foreground">{item?.type}</div>
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(item?.status)}`}>
                      {item?.status}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Quantity:</span>
                      <span className="ml-1 font-medium text-foreground">{item?.quantity}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Unit Rate:</span>
                      <span className="ml-1 font-medium text-foreground">{formatCurrency(item?.unitRate)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total:</span>
                      <span className="ml-1 font-medium text-accent">{formatCurrency(item?.totalCost)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="pt-4 border-t border-border">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium text-foreground">Total Branding Cost</span>
                <span className="text-xl font-bold text-primary">{formatCurrency(totalBrandingCost)}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Activity History</h3>
            
            <div className="space-y-4">
              {mockHistory?.map((entry) => (
                <div key={entry?.id} className="flex gap-4">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name="Clock" size={14} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-foreground">{entry?.action}</span>
                      <span className="text-sm text-muted-foreground">{entry?.timestamp}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">{entry?.details}</p>
                    <span className="text-xs text-muted-foreground">by {entry?.user}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreDetailPanel;